// Typed wrappers over the PMS broker-auth BFF endpoints (PMS docs/broker-auth.md).
// PMS is the backend-for-frontend: it holds the Cognito app-client secret and
// calls Cognito server-side, so this SPA only ever speaks to PMS. Access + ID
// tokens come back in the JSON body (held in memory by the auth context); the
// refresh token rides in an httpOnly cookie PMS sets, never readable here.

import {
  apiRequest,
  type ApiResult,
  type FileResponse,
  type UploadProgressEvent,
} from "@/lib/api";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/browser";

const AUTH_BASE = "/api/broker/auth";

/** Tokens returned by login / set-password / refresh. */
export interface AuthTokens {
  accessToken: string;
  idToken: string;
  expiresIn: number;
  tokenType: "Bearer";
}

/** First-login challenge: the broker must set a new password before tokens issue. */
export interface NewPasswordChallenge {
  challenge: "NEW_PASSWORD_REQUIRED";
  session: string;
  username: string;
}

/** TOTP MFA challenge: the broker must enter an authenticator code to finish login. */
export interface MfaChallenge {
  challenge: "SOFTWARE_TOKEN_MFA";
  session: string;
  username: string;
}

/** A challenge the login flow can hand back instead of tokens. */
export type LoginChallenge = NewPasswordChallenge | MfaChallenge;

/** Login either authenticates outright or returns a challenge to complete first. */
export type LoginResult =
  | { kind: "tokens"; tokens: AuthTokens }
  | { kind: "challenge"; challenge: LoginChallenge };

/** A sign-in method the capability-adaptive login screen can offer (PMS-S048). */
export type BrokerAuthMethod = "password" | "email_otp" | "passkey";

/** Result of the /begin capability probe: the methods valid for the account. */
export interface BeginResult {
  session?: string;
  username: string;
  methods: BrokerAuthMethod[];
  // Set when email-OTP is the account's only factor: Cognito auto-initiated the
  // challenge during /begin and already emailed a code, handing back `session`.
  // The SPA reuses that session instead of sending a second code (BRK-S025).
  emailOtpSent?: boolean;
  destination?: string;
}

/** POST /api/broker/auth/begin — probe which sign-in methods this account can
 *  use, so the login screen shows only those (no dead buttons). Non-enumerating:
 *  unknown accounts return a uniform set; only a real outage rejects. */
export function beginAuth(email: string): Promise<BeginResult> {
  return apiRequest<BeginResult>(`${AUTH_BASE}/begin`, {
    method: "POST",
    body: { email },
  });
}

/** Broker authorization role (PMS-S060). An open string set on the wire — the
 *  SPA never hard-codes behavior on a specific role, it reads `capabilities`. */
export type BrokerRole = "owner" | "staff";

/** A capability the broker holds, and the scope it applies at (PMS-S060). */
export type BrokerCapability =
  | "brokerage:view"
  | "brokerage:edit"
  | "broker:view"
  | "broker:edit"
  | "account:view"
  | "account:edit"
  // In-app inbox (PMS-S353 / BRK-S157): view the broker's own notification feed.
  | "notification:view"
  // Preference center (PMS-S351/S357 + S346 / BRK-S158): view/edit notification
  // delivery preferences, and view/edit marketing-communication consent.
  | "notification-preference:view"
  | "notification-preference:edit"
  | "consent:view"
  | "consent:edit";
export type CapabilityScope = "all" | "own";
export interface Capability {
  capability: BrokerCapability;
  scope: CapabilityScope;
}

/** The broker's authentication posture (PMS-S073): which credentials/factors are
 *  set. Non-secret status booleans the SPA uses to decide what (if anything) to
 *  nudge after login, and to render the /security password card. Read from the
 *  dedicated GET /api/broker/security-status (kept off the hot /me path) — see
 *  `getSecurityStatus`. Mirrors the PMS contract (PMS docs/broker-auth.md). */
export interface BrokerSecurityPosture {
  /** Whether a password credential is set (false for an invite-only OTP broker). */
  hasPassword: boolean;
  /** Enrolled multi-factor methods (each requires a password as the first factor). */
  mfa: { totp: boolean; sms: boolean };
  /** Number of registered passkeys (a passkey is its own first factor). */
  passkeyCount: number;
  /** Server-computed `passkeyCount > 0`, sent alongside for convenience. */
  hasPasskey?: boolean;
}

/** The authenticated broker's identity from GET /api/broker/me. Carries the
 *  role + the capabilities it grants (computed server-side, PMS-S060) — the SPA
 *  shows/hides edit controls off these, never off the role string directly. */
export interface BrokerIdentity {
  brokerUserId: string;
  brokerId: string;
  brokerageId: string;
  email: string;
  sub: string;
  role: BrokerRole;
  capabilities: Capability[];
}

/** A "strong" sign-in factor is a passkey or any MFA — phishing-resistant or a
 *  real second factor, as opposed to a password alone (BRK-S026). When the posture
 *  is UNKNOWN (null — a PMS not serving PMS-S073, or a failed status read) we treat
 *  the broker as already protected so the onboarding nudge never traps anyone. */
export function hasStrongFactor(
  posture: BrokerSecurityPosture | null,
): boolean {
  if (!posture) return true;
  return (
    (posture.hasPasskey ?? posture.passkeyCount > 0) ||
    posture.mfa.totp ||
    posture.mfa.sms
  );
}

/** Whether to show the guided "secure your account" prompt: a known posture that
 *  lacks any strong factor (BRK-S026). */
export function needsSecurityOnboarding(
  posture: BrokerSecurityPosture | null,
): boolean {
  return posture != null && !hasStrongFactor(posture);
}

/** Whether the broker holds a capability — the UI's show/hide gate (PMS-S060). */
export function hasCapability(
  identity: BrokerIdentity | null,
  capability: BrokerCapability,
): boolean {
  return (
    identity?.capabilities.some((c) => c.capability === capability) ?? false
  );
}

function asChallenge(value: unknown): LoginChallenge | null {
  if (typeof value === "object" && value !== null) {
    const name = (value as { challenge?: unknown }).challenge;
    if (name === "NEW_PASSWORD_REQUIRED" || name === "SOFTWARE_TOKEN_MFA") {
      return value as LoginChallenge;
    }
  }
  return null;
}

/** POST /api/broker/auth/login — email + password. */
export async function login(
  email: string,
  password: string,
): Promise<LoginResult> {
  const data = await apiRequest<AuthTokens | LoginChallenge>(
    `${AUTH_BASE}/login`,
    { method: "POST", body: { email, password } },
  );
  const challenge = asChallenge(data);
  return challenge
    ? { kind: "challenge", challenge }
    : { kind: "tokens", tokens: data as AuthTokens };
}

/** POST /api/broker/auth/set-password — complete the NEW_PASSWORD_REQUIRED challenge. */
export function setPassword(input: {
  username: string;
  session: string;
  newPassword: string;
}): Promise<AuthTokens> {
  return apiRequest<AuthTokens>(`${AUTH_BASE}/set-password`, {
    method: "POST",
    body: input,
  });
}

/** POST /api/broker/auth/refresh — exchange the httpOnly cookie for fresh tokens. */
export function refresh(): Promise<AuthTokens> {
  return apiRequest<AuthTokens>(`${AUTH_BASE}/refresh`, { method: "POST" });
}

/** POST /api/broker/auth/logout — best-effort revoke + clear the cookie (idempotent). */
export async function logout(): Promise<void> {
  await apiRequest<{ ok: true }>(`${AUTH_BASE}/logout`, { method: "POST" });
}

/** POST /api/broker/auth/forgot-password — start a reset (always success-shaped). */
export async function forgotPassword(email: string): Promise<void> {
  await apiRequest<{ ok: true }>(`${AUTH_BASE}/forgot-password`, {
    method: "POST",
    body: { email },
  });
}

/** POST /api/broker/auth/reset-password — complete a reset with the emailed code. */
export async function resetPassword(input: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<void> {
  await apiRequest<{ ok: true }>(`${AUTH_BASE}/reset-password`, {
    method: "POST",
    body: input,
  });
}

/** POST /api/broker/auth/mfa — complete a SOFTWARE_TOKEN_MFA login challenge. */
export function completeMfa(input: {
  username: string;
  session: string;
  code: string;
}): Promise<AuthTokens> {
  return apiRequest<AuthTokens>(`${AUTH_BASE}/mfa`, {
    method: "POST",
    body: input,
  });
}

/** The challenge session returned when starting passwordless email-OTP login. */
export interface PasswordlessStart {
  session: string;
  username: string;
  destination?: string;
}

/** POST /api/broker/auth/passwordless/start — email a sign-in code. Always
 *  success-shaped (non-enumerating); only a real outage rejects. */
export function startPasswordless(email: string): Promise<PasswordlessStart> {
  return apiRequest<PasswordlessStart>(`${AUTH_BASE}/passwordless/start`, {
    method: "POST",
    body: { email },
  });
}

/** POST /api/broker/auth/passwordless/verify — complete email-OTP login. */
export function verifyPasswordless(input: {
  username: string;
  session: string;
  code: string;
}): Promise<AuthTokens> {
  return apiRequest<AuthTokens>(`${AUTH_BASE}/passwordless/verify`, {
    method: "POST",
    body: input,
  });
}

/** GET /api/broker/me — the authenticated broker (Bearer access token). */
export function getMe(accessToken: string): Promise<BrokerIdentity> {
  return apiRequest<BrokerIdentity>("/api/broker/me", { accessToken });
}

// ─── TOTP MFA enrollment (broker-guarded) ─────────────────────────────────────
const MFA_BASE = "/api/broker/mfa/totp";

// A requester for authenticated /api/broker/** calls, supplied by the auth
// context (AuthProvider.authedRequest): it injects the access token and
// transparently refreshes-and-retries on a 401. Broker-guarded wrappers take
// this instead of a raw token so they inherit session resilience (BRK-S015).
export interface AuthedRequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
}
export type AuthedRequester = <T>(
  path: string,
  opts?: AuthedRequestOptions,
) => Promise<T>;

// The auth context's concrete requester also exposes `.withStatus` — the same
// call resolving the HTTP status too, for the rare caller that must branch on the
// exact 2xx code. Wrappers that don't need the status keep taking the plain
// `AuthedRequester` (this is assignable to it).
/**
 * An authenticated BYTES read (BRK-S133) — the same session resilience the JSON
 * requester has, for the one broker surface that reads a file.
 *
 * A separate type rather than an option on `AuthedRequester`, because the
 * return type differs and a `Promise<T>` that is sometimes a `Blob` is how a
 * caller ends up `JSON.parse`ing a PDF.
 */
export type AuthedBlobRequester = (path: string) => Promise<FileResponse>;

/**
 * An authenticated FILE POST (BRK-S141) — the same session resilience, for the
 * one broker surface that writes a file through the API.
 *
 * Its own type for the same reason `AuthedBlobRequester` is: the BODY differs
 * (a `FormData`, which must not be JSON-stringified) and folding it into
 * `AuthedRequestOptions` would make `body` mean two things depending on a
 * caller's intent.
 */
export type AuthedUploader = <T>(path: string, form: FormData) => Promise<T>;

/**
 * An authenticated FILE POST that reports its PROGRESS (BRK-S145).
 *
 * Separate from `AuthedUploader` because the transport differs, not just the
 * options: progress needs `XMLHttpRequest`, and folding an `onProgress` onto
 * the type above would promise a callback the `fetch`-based uploader can never
 * call. A caller that does not care keeps taking `AuthedUploader`.
 */
export type AuthedProgressUploader = <T>(
  path: string,
  form: FormData,
  options?: { onProgress?: (progress: UploadProgressEvent) => void },
) => Promise<T>;

export interface AuthedRequesterWithStatus {
  <T>(path: string, opts?: AuthedRequestOptions): Promise<T>;
  withStatus<T>(
    path: string,
    opts?: AuthedRequestOptions,
  ): Promise<ApiResult<T>>;
}

// ─── Broker self-service — own brokerage + profile (PMS-S063) ──────────────────
// Capability-gated server-side; here they're plain authed wrappers. The PUT
// bodies are deliberately narrow (the server ignores anything else): a broker
// edits their brokerage's name/affiliation/address, and their own
// name/phone/business unit — never the sign-in email or the parent brokerage.

/** The broker's own brokerage, legal address resolved inline. */
export interface BrokerBrokerage {
  id: string;
  name: string;
  type: string;
  licenseNumber: string;
  affiliation: string | null;
  legalAddress: {
    street: string;
    city: string;
    state: string;
    zip: string;
  } | null;
}

export interface BrokerageUpdate {
  name?: string;
  affiliation?: string | null;
  legalAddress?: { street: string; city: string; state: string; zip: string };
}

/** The broker's own profile. `email` is the sign-in identity (not editable here). */
export interface BrokerProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  businessUnit: string | null;
  licenseNumber: string;
}

export interface ProfileUpdate {
  firstName?: string;
  lastName?: string;
  phone?: string;
  businessUnit?: string | null;
}

/** GET /api/broker/brokerage — the broker's own brokerage (all roles). */
export function getOwnBrokerage(
  request: AuthedRequester,
): Promise<BrokerBrokerage> {
  return request<BrokerBrokerage>("/api/broker/brokerage");
}

/** PUT /api/broker/brokerage — edit the brokerage (owner only; 403 otherwise). */
export function updateOwnBrokerage(
  request: AuthedRequester,
  input: BrokerageUpdate,
): Promise<BrokerBrokerage> {
  return request<BrokerBrokerage>("/api/broker/brokerage", {
    method: "PUT",
    body: input,
  });
}

/** GET /api/broker/profile — the broker's own record (all roles). */
export function getOwnProfile(
  request: AuthedRequester,
): Promise<BrokerProfile> {
  return request<BrokerProfile>("/api/broker/profile");
}

/** PUT /api/broker/profile — edit own name/phone/business unit (all roles). */
export function updateOwnProfile(
  request: AuthedRequester,
  input: ProfileUpdate,
): Promise<BrokerProfile> {
  return request<BrokerProfile>("/api/broker/profile", {
    method: "PUT",
    body: input,
  });
}

/** GET /api/broker/security-status — the broker's posture (PMS-S073). A
 *  dedicated endpoint (not on /me, which stays cheap on the hot auth path);
 *  broker-guarded via the authed requester. */
export function getSecurityStatus(
  request: AuthedRequester,
): Promise<BrokerSecurityPosture> {
  return request<BrokerSecurityPosture>("/api/broker/security-status");
}

/** Set or change the broker's own password (PMS-S073). Omit `currentPassword`
 *  to SET a first password (an invite-only broker who signed in via email-OTP and
 *  has none yet — the authenticated session is the proof of identity); include it
 *  to CHANGE an existing one. Broker-guarded; Cognito stays the authoritative
 *  validator (a policy violation comes back as a handled 400 in the { error }
 *  envelope). Distinct from the pre-auth NEW_PASSWORD_REQUIRED challenge and the
 *  emailed forgot/reset flow — this is the in-session path (BRK-S026). */
export async function updateOwnPassword(
  request: AuthedRequester,
  input: { currentPassword?: string; newPassword: string },
): Promise<void> {
  await request<{ ok: true }>("/api/broker/password", {
    method: "POST",
    body: input,
  });
}

/** GET /api/broker/mfa — whether the broker currently has TOTP MFA enabled. */
export function getMfaStatus(
  request: AuthedRequester,
): Promise<{ totp: boolean }> {
  return request<{ totp: boolean }>("/api/broker/mfa");
}

/** The secret + ready-to-render otpauth:// URI returned when starting enrollment. */
export interface TotpAssociation {
  secretCode: string;
  otpauthUri: string;
}

/** POST /api/broker/mfa/totp/associate — begin TOTP enrollment. */
export function associateTotp(
  request: AuthedRequester,
): Promise<TotpAssociation> {
  return request<TotpAssociation>(`${MFA_BASE}/associate`, { method: "POST" });
}

/** POST /api/broker/mfa/totp/verify — confirm the first code and enable TOTP. */
export async function verifyTotp(
  request: AuthedRequester,
  code: string,
): Promise<void> {
  await request<{ ok: true }>(`${MFA_BASE}/verify`, {
    method: "POST",
    body: { code },
  });
}

/** POST /api/broker/mfa/totp/disable — turn TOTP off. */
export async function disableTotp(request: AuthedRequester): Promise<void> {
  await request<{ ok: true }>(`${MFA_BASE}/disable`, { method: "POST" });
}

// ─── Passkeys / WebAuthn (PMS-S042) ───────────────────────────────────────────
// PMS relays the opaque WebAuthn JSON both ways: register/login *start* return
// the credential-creation/request options the browser feeds to
// navigator.credentials, and *complete*/verify take the attestation/assertion
// the browser produces. The SPA runs the ceremony (src/lib/passkeys.ts); these
// wrappers are just the typed transport. Registration is broker-guarded (via the
// authed requester); passkey login is public (it *is* the sign-in).
const PASSKEYS_BASE = "/api/broker/passkeys";

/** A registered passkey, as listed for the account-security management view. */
export interface PasskeyDescriptor {
  credentialId: string;
  friendlyName?: string;
  createdAt?: string;
}

/** POST /api/broker/passkeys/register/start — credential-creation options. */
export function startPasskeyRegistration(
  request: AuthedRequester,
): Promise<{ options: PublicKeyCredentialCreationOptionsJSON }> {
  return request(`${PASSKEYS_BASE}/register/start`, { method: "POST" });
}

/** POST /api/broker/passkeys/register/complete — register the SPA's attestation. */
export async function completePasskeyRegistration(
  request: AuthedRequester,
  credential: RegistrationResponseJSON,
): Promise<void> {
  await request<{ ok: true }>(`${PASSKEYS_BASE}/register/complete`, {
    method: "POST",
    body: { credential },
  });
}

/** GET /api/broker/passkeys — the broker's registered passkeys. */
export function listPasskeys(
  request: AuthedRequester,
): Promise<{ passkeys: PasskeyDescriptor[] }> {
  return request(PASSKEYS_BASE);
}

/** POST /api/broker/passkeys/delete — remove one of the broker's passkeys. */
export async function deletePasskey(
  request: AuthedRequester,
  credentialId: string,
): Promise<void> {
  await request<{ ok: true }>(`${PASSKEYS_BASE}/delete`, {
    method: "POST",
    body: { credentialId },
  });
}

/** The challenge session + credential-request options for a passkey sign-in. */
export interface PasskeyLoginStart {
  session: string;
  username: string;
  options: PublicKeyCredentialRequestOptionsJSON;
}

/** POST /api/broker/auth/passkey/start — begin passkey sign-in for an email. */
export function startPasskeyLogin(email: string): Promise<PasskeyLoginStart> {
  return apiRequest<PasskeyLoginStart>(`${AUTH_BASE}/passkey/start`, {
    method: "POST",
    body: { email },
  });
}

/** POST /api/broker/auth/passkey/verify — complete passkey sign-in. */
export function verifyPasskeyLogin(input: {
  username: string;
  session: string;
  credential: AuthenticationResponseJSON;
}): Promise<AuthTokens> {
  return apiRequest<AuthTokens>(`${AUTH_BASE}/passkey/verify`, {
    method: "POST",
    body: input,
  });
}
